import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useNow } from '../../hooks/useNow';
import { closedDaysLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { toMinutes, windowFor } from '../../core/rules/schedule';
import type { OpeningDay } from '../../core/types/site';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';

/** Tijdelijk sfeerbeeld, niet de salon zelf: vervangen vóór livegang (docs/BEELDEN.md). */
const ROOM = 'https://cdn.magicpatterns.com/patterns/generated-images/7166f87a-c402-483f-8556-b060455341f8.jpg';

/**
 * "Kom langs": het laatste blok van de homepage, op blush. Het beeld staat in een boog, als
 * een deuropening; bij het scrollen stap je er een fractie naar binnen (het beeld zoomt uit).
 * Een kleine regel zegt of er vandaag afspraken zijn (de salon werkt alleen op afspraak).
 * Draagt ook de lokale vindbaarheid (stad in de kop, adres als tekst).
 * Adres en dagen komen uit Instellingen › Salon & contact en › Salondagen; de woorden en het beeld uit Paginateksten.
 */
export function HomeVisit() {
  const pages = usePages();
  const ref = useRef<HTMLDivElement>(null);
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const now = useNow();
  const copy = pages.home.visit;
  const media = mediaOr(copy.media, {
    image: ROOM,
    alt: 'Sfeerbeeld: een behandelbed met linnen in daglicht'
  });
  const {
    openingHours
  } = useSite();
  const closed = closedDaysLabel(openingHours);
  const status = todayStatus(openingHours, now);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.6], [1.15, 1]);
  return <section className="bg-blush" aria-labelledby="home-visit" data-id="element-7513" data-mp="8g504mhk7ovz">
      <div className="mx-auto grid max-w-content gap-12 px-6 py-20 sm:py-24 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10 lg:py-32" data-id="element-7514" data-mp="8hg1ygrg2mkg">
        <div ref={ref} className="mx-auto w-full max-w-xs sm:max-w-sm lg:col-span-5 lg:max-w-none" data-id="element-7515" data-mp="iecgp47xs4xg">
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full bg-sand" data-id="element-7516" data-mp="dfhx4025acca">
            <motion.div style={reduce ? undefined : {
            scale
          }} className="absolute inset-0" data-id="element-7517" data-mp="rf0vi155m9be">
              <MediaView media={media} sizes="(min-width: 1024px) 38vw, 100vw" className="h-full w-full" data-id="element-7518" data-mp="ndfj0gwj7x1q" />
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pb-4" data-id="element-7519" data-mp="2t6vzdul659j2">
          <p className="flex items-center gap-2.5 text-[14px] text-ink" data-id="element-7520" data-mp="2p52461tqsxqa">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${status.open ? 'bg-sage' : 'bg-ink/30'}`} data-id="element-7521" data-mp="2p7535bcvi175" />
            {status.label}
          </p>
          <h2 id="home-visit" className={`mt-5 ${sectionHeading} text-ink`} data-id="element-7522" data-mp="2j1dz464s9tkv">
            <HeadingText heading={copy.heading} accentBlock data-id="element-7523" data-mp="25bkbts8xfhd9" />
          </h2>
          {copy.body && <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7524" data-mp="2a9vkhznhjltw">{fill(copy.body)}</p>}

          <address className="mt-8 flex max-w-lg gap-3 border-t border-ink/15 pt-6 text-[15px] not-italic leading-relaxed text-ink" data-id="element-7525" data-mp="1vd4kpp9wg83r">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-7526" data-mp="20cqvzt663e9x" />
            <span data-id="element-7527" data-mp="1mdr66i6x4tc6">
              {salon.street}, {salon.postalCode} {salon.locality}
              <span className="block text-muted" data-id="element-7528" data-mp="1rcasijpfc752">
                Alleen op afspraak{closed ? ` · ${closed.toLowerCase()}` : ''}
              </span>
            </span>
          </address>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-7529" data-mp="3lpk7us4nimv8">
            {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-ink px-7 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98]" data-id="element-7530" data-mp="1lm9kgtd89lre">
                {fill(copy.cta)}
              </button>}
            {copy.secondaryCta && <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7531" data-mp="xwoptd7pp02a">
                {fill(copy.secondaryCta)}
                <span className="sr-only" data-id="element-7532" data-mp="nzkbfzk3y8w1"> {shopLabels.newTab}</span>
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7533" data-mp="nx7jquojsk5s" />
              </a>}
          </div>
          <Link to="/contact" className="mt-2 inline-flex min-h-[44px] items-center text-[14px] text-muted underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7534" data-mp="eyukpa9pf8p6">
            Openingstijden, parkeren en aanbellen
          </Link>
        </div>
      </div>
    </section>;
}

/**
 * De salon werkt alleen op afspraak, dus nooit "open": wel wanneer er vandaag afspraken zijn.
 * "Vandaag op afspraak, 13:00 – 20:00" / "Vandaag geen afspraken meer" / "Vandaag gesloten". Puur.
 */
function todayStatus(hours: OpeningDay[], now: Date): {
  open: boolean;
  label: string;
} {
  const w = windowFor(hours, now.getDay());
  if (!w) return {
    open: false,
    label: 'Vandaag gesloten'
  };
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes >= toMinutes(w.close)) return {
    open: false,
    label: 'Vandaag geen afspraken meer'
  };
  return {
    open: true,
    label: `Vandaag op afspraak, ${w.open} – ${w.close}`
  };
}